import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from '../../components/Header/Header';
import PublicListPromoBar from '../../components/PublicListPromoBar';
import { tickerApi } from '../../services/tickerApi';
import TabBar from '../../components/Common/TabBar';
import { UpArrowIcon, DownArrowIcon } from '../../components/Icons';
import { TickerPast4QuartersAnalystViewsData, QuarterData, TickerAnalystView } from '../../types/analystViews';
import { useAuth } from '../../contexts/AuthContext';
import { LockIcon } from '../../components/StockTable';
import { useFontSize } from '../../contexts/FontSizeContext';
import { getTextSizeClass, getHeaderTextSizeClass, getStatisticLabelSizeClass, getSmallTextSizeClass } from '../../utils/fontSizeUtils';
import { saveUserTickerPreferences, loadUserTickerPreferences } from '../../utils/tickerPreferencesUtils';
import Footer from '../../components/Footer';
import CategoryInfoModal from '../../components/CategoryInfoModal/CategoryInfoModal';
import IndicatorInfoModal from '../../components/IndicatorInfoModal/IndicatorInfoModal';
import { calculateTickerTechnicalsStatisticsColumnWidth } from '../../utils/tickerPageColumnUtils';
import {
  formatQuarterHeader,
  formatAnalystViewsDate,
  transformAnalystViewsData,
  formatAnalystViewsValue,
  getAnalystViewsStatisticColor,
  getAnalystViewsQuarterData,
  calculateAnalystViewsStatisticsColumnWidth,
} from '../../utils/tickerAnalystViewsUtils';
import { publicListApi } from '../../services/publicListApi';
import { useTickerPageMeta } from '../../hooks/useTickerPageMeta';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

// Analyst views statistics configuration organized by category (same as ListAnalystViews)
const ANALYST_VIEWS_STATISTICS = [
  // Price Targets
  { key: 'priceTargetRange', label: 'Price Target Range', type: 'financial' },
  { key: 'averagePriceTarget', label: 'Average Price Target', type: 'financial' },
  { key: 'lastClosingPrice', label: 'Last Closing Price', type: 'financial' },
  { key: 'priceTargetDifference', label: 'Potential Upside', type: 'ratio' },
  
  // Recommendations
  { key: 'averageRecommendation', label: 'Average Recommendation', type: 'financial' },
  { key: 'strongBuyCount', label: '# of Strong Buy', type: 'financial' },
  { key: 'buyCount', label: '# of Buy', type: 'financial' },
  { key: 'holdCount', label: '# of Hold', type: 'financial' },
  { key: 'sellCount', label: '# of Sell', type: 'financial' },
  { key: 'strongSellCount', label: '# of Strong Sell', type: 'financial' },
  { key: 'strongBuyPercent', label: '% Strong Buy', type: 'ratio' },
  { key: 'buyPercent', label: '% Buy', type: 'ratio' },
  { key: 'holdPercent', label: '% Hold', type: 'ratio' },
  { key: 'sellPercent', label: '% Sell', type: 'ratio' },
  { key: 'strongSellPercent', label: '% Strong Sell', type: 'ratio' },
  
  // Earnings
  { key: 'nextEarningsDate', label: 'Next Earnings Date', type: 'date' },
  { key: 'thisQuarterEpsAvg', label: 'Current Quarter EPS Average Estimate', type: 'financial' },
  { key: 'thisQuarterEpsRange', label: 'Current Quarter EPS Estimate Range', type: 'financial' },
  { key: 'nextQuarterEpsAvg', label: 'Next Quarter EPS Average Estimate', type: 'financial' },
  { key: 'nextQuarterEpsRange', label: 'Next Quarter EPS Estimate Range', type: 'financial' },
  { key: 'thisYearEpsAvg', label: 'Current Year EPS Average Estimate', type: 'financial' },
  { key: 'thisYearEpsRange', label: 'Current Year EPS Estimate Range', type: 'financial' },
  { key: 'nextYearEpsAvg', label: 'Next Year EPS Average Estimate', type: 'financial' },
  { key: 'nextYearEpsRange', label: 'Next Year EPS Estimate Range', type: 'financial' },
];

// Category headers for organizing statistics
const CATEGORY_HEADERS = [
  { startIndex: 0, label: 'Price Targets' },
  { startIndex: 4, label: 'Recommendations' },
  { startIndex: 15, label: 'Earnings Estimates' },
];


const TickerAnalystViews: React.FC = () => {
  const { ticker } = useParams<{ ticker: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check for public_uuid query parameter
  const searchParams = new URLSearchParams(location.search);
  const publicUuid = searchParams.get('public_uuid');
  const { isAuthenticated, user } = useAuth();
  const { fontSize } = useFontSize();
  
  // Pro-only statistics (always show PRO tag, regardless of subscription status)
  const PRO_ONLY_STATISTICS: string[] = [];
  
  // Public list state
  const [publicListData, setPublicListData] = useState<{
    ownerHasProSubscription: boolean;
  } | null>(null);
  
  const isPublicList = !!publicUuid;
  
  // Determine locked statistics based on pro subscription status (for actually locking values)
  // For public lists, use owner's subscription status instead of viewer's
  const lockedStatistics = React.useMemo(() => {
    if (isPublicList && publicListData) {
      return publicListData.ownerHasProSubscription ? [] : PRO_ONLY_STATISTICS;
    }
    return (!isAuthenticated || !user?.hasProSubscription) ? PRO_ONLY_STATISTICS : [];
  }, [isPublicList, publicListData, isAuthenticated, user?.hasProSubscription]);

  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TickerPast4QuartersAnalystViewsData | null>(null);
  const [tickerName, setTickerName] = useState<string>(() => {
    const cachedName = sessionStorage.getItem(`ticker-name-${ticker}`);
    return cachedName || '';
  });
  const [tickerSymbol, setTickerSymbol] = useState<string>(() => {
    const cachedSymbol = sessionStorage.getItem(`ticker-symbol-${ticker}`);
    return cachedSymbol ?? ticker ?? '';
  });
  const [tickerBasicsId, setTickerBasicsId] = useState<number | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [marketType, setMarketType] = useState<string | null>(() => {
    const cachedMarketType = sessionStorage.getItem(`ticker-market-type-${ticker}`);
    return cachedMarketType || null;
  });
  const [priceData, setPriceData] = useState<any>(null);
  const [latestAnalystViewDate, setLatestAnalystViewDate] = useState<string | null>(null);
  const [nextEarningsDate, setNextEarningsDate] = useState<Date | null>(null);

  // Set document title and meta description for ticker analyst views page
  useTickerPageMeta({
    tickerSymbol,
    tickerName,
    pageType: 'analysts',
  });
  
  // Polling state
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncPollingAttempts, setSyncPollingAttempts] = useState(0);
  const [pollingLimitReached, setPollingLimitReached] = useState(false);
  const pollingStoppedRef = useRef(false);
  const syncPollingIntervalRef = useRef<NodeJS.Timeout | null>(null); // Use ref for synchronous access
  const isCheckingSyncRef = useRef(false); // Flag to prevent concurrent sync checks
  const currentAttemptRef = useRef(1); // Track current attempt number for interval callback
  const tableScrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Full screen state
  const [isFullScreen, setIsFullScreen] = useState(false);
  useBodyScrollLock(isFullScreen);

  // Fetch public list data when publicUuid is present
  useEffect(() => {
    if (publicUuid && !publicListData) {
      publicListApi.getPublicList(publicUuid)
        .then((response) => {
          setPublicListData({
            ownerHasProSubscription: response.data.owner_has_pro_subscription,
          });
        })
        .catch((error) => {
          console.error('Failed to fetch public list:', error);
          // If public list fetch fails, treat as if owner doesn't have PRO
          setPublicListData({
            ownerHasProSubscription: false,
          });
        });
    } else if (!publicUuid) {
      setPublicListData(null);
    }
  }, [publicUuid, publicListData]);
  
  // Handle keyboard events for full screen mode
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullScreen) {
        setIsFullScreen(false);
      }
    };

    if (isFullScreen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullScreen]);
  
  // Edit mode state
  const [isEditingColumns, setIsEditingColumns] = useState(false);
  const [hiddenStatistics, setHiddenStatistics] = useState<string[]>(() => {
    const saved = sessionStorage.getItem(`stocktable-hidden-statistics-${ticker}-analyst-views`);
    return saved ? JSON.parse(saved) : [];
  });
  const [tempHiddenStatistics, setTempHiddenStatistics] = useState<string[]>([]);
  
  // Category info modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [isIndicatorModalOpen, setIsIndicatorModalOpen] = useState(false);
  const [selectedIndicatorKey, setSelectedIndicatorKey] = useState<string>('');


  // Function to clear interval without setting stopped flag (for cleanup/reset)
  const clearPollingInterval = () => {
    if (syncPollingIntervalRef.current) {
      clearInterval(syncPollingIntervalRef.current);
      syncPollingIntervalRef.current = null;
    }
  };

  // Stop polling
  const stopPolling = () => {
    pollingStoppedRef.current = true;
    clearPollingInterval();
    setIsSyncing(false);
  };

  // Fetch price data for the ticker
  const fetchPriceData = async (tickerSymbol: string) => {
    try {
      const response = await tickerApi.getTickerLatestPrice({
        ticker_symbols: [tickerSymbol],
      });

      if (response.success && response.data && response.data.tickers) {
        const tickerPriceData = response.data.tickers.find((p: any) => p.ticker_symbol === tickerSymbol);
        if (tickerPriceData && tickerPriceData.prices && tickerPriceData.prices.length > 0) {
          const latestPrice = tickerPriceData.prices[0];
          const previousPrice = tickerPriceData.prices.length > 1 ? tickerPriceData.prices[1] : null;
          
          setPriceData({
            ticker_symbol: tickerPriceData.ticker_symbol,
            ticker_basics_id: tickerPriceData.ticker_basics_id,
            close_price_adjusted: latestPrice.close_price_adjusted,
            date: new Date(latestPrice.date),
            previous_close_price_adjusted: previousPrice ? previousPrice.close_price_adjusted : null,
            previous_date: previousPrice ? new Date(previousPrice.date) : null,
            currentPrice: latestPrice.close_price_adjusted,
            previousPrice: previousPrice ? previousPrice.close_price_adjusted : null,
            change: latestPrice.change,
            changePercent: latestPrice.changePercent,
            volume: latestPrice.volume,
            high: latestPrice.high,
            low: latestPrice.low,
            open: latestPrice.open,
            prevClose: latestPrice.previousClose,
          });
        }
      }
    } catch (error) {
      console.error('Error fetching price data:', error);
    }
  };

  // Check sync status and fetch data
  const checkSyncAndFetchData = async (attemptNumber: number = 1) => {
    if (!ticker) return;
    
    // Guard: Check if we've exceeded the attempt limit before proceeding
    if (attemptNumber > 5) {
      console.warn(`TickerAnalystViews: Attempt ${attemptNumber} exceeds limit (5), stopping`);
      stopPolling();
      setPollingLimitReached(true);
      isCheckingSyncRef.current = false;
      return;
    }
    
    // Guard: Don't proceed if polling has been stopped (e.g., after reaching limit or 429 error)
    if (pollingStoppedRef.current) {
      console.log(`TickerAnalystViews: checkSyncAndFetchData called but polling is stopped, ignoring`);
      isCheckingSyncRef.current = false;
      return;
    }
    
    // Guard: Prevent concurrent execution - if a sync check is already in progress, ignore this call
    if (isCheckingSyncRef.current) {
      console.log(`TickerAnalystViews: checkSyncAndFetchData already in progress, ignoring duplicate call (attempt ${attemptNumber})`);
      return;
    }
    
    // Set flag to prevent concurrent calls
    isCheckingSyncRef.current = true;

    console.log(`TickerAnalystViews: Checking sync status for ${ticker} (attempt ${attemptNumber})`);
    
    // Update global polling attempts for display purposes
    setSyncPollingAttempts(attemptNumber);

    try {
      // Check sync status first
      const syncResponse = await tickerApi.checkTickerSyncStatus({
        ticker_symbols: [ticker],
      });

      if (syncResponse.success && syncResponse.data && syncResponse.data.tickers) {
        const tickerData = syncResponse.data.tickers.find((t: any) => t.ticker_symbol === ticker);
        
        if (!tickerData) {
          throw new Error(`Ticker ${ticker} not found`);
        }
        
        // Store ticker name and symbol
        if (tickerData.ticker_name) {
          const name = tickerData.ticker_name;
          setTickerName(name);
          sessionStorage.setItem(`ticker-name-${ticker}`, name);
        }
        if (tickerData.ticker_symbol) {
          const symbol = tickerData.ticker_symbol;
          setTickerSymbol(symbol);
          sessionStorage.setItem(`ticker-symbol-${ticker}`, symbol);
        }
        
        // Set lastUpdated from last_sync_ended_at
        if (tickerData.last_sync_ended_at) {
          const date = typeof tickerData.last_sync_ended_at === 'string' 
            ? new Date(tickerData.last_sync_ended_at) 
            : tickerData.last_sync_ended_at;
          if (date instanceof Date && !isNaN(date.getTime())) {
            setLastUpdated(date);
          }
        }
        
        if (tickerData.is_synced) {
          console.log(`TickerAnalystViews: ${ticker} is synced, fetching data`);
          
          // Fetch past 4 quarters analyst views data
          const analystViewsResponse = await tickerApi.getPast4QuartersAnalystViews({
            ticker_basics_id: tickerData.ticker_basics_id,
            category: 'all',
            key: 'all'
          });

          if (analystViewsResponse.success && analystViewsResponse.data) {
            setData(analystViewsResponse.data);
            setTickerBasicsId(tickerData.ticker_basics_id);
            
            // Fetch market_type from ticker basics
            try {
              const tickerBasicsResponse = await tickerApi.getTickerBasics([ticker.toUpperCase()]);
              if (tickerBasicsResponse.success && tickerBasicsResponse.data && tickerBasicsResponse.data.length > 0) {
                const tickerBasic = tickerBasicsResponse.data.find((tb: any) => tb.tickerSymbol === ticker.toUpperCase());
                if (tickerBasic) {
                  if (tickerBasic.marketType) {
                    setMarketType(tickerBasic.marketType);
                    sessionStorage.setItem(`ticker-market-type-${ticker}`, tickerBasic.marketType);
                  }
                  if (tickerBasic.nextEarningsDate) {
                    const date = typeof tickerBasic.nextEarningsDate === 'string' 
                      ? new Date(tickerBasic.nextEarningsDate) 
                      : tickerBasic.nextEarningsDate;
                    if (date instanceof Date && !isNaN(date.getTime())) {
                      setNextEarningsDate(date);
                    }
                  }
                }
              }
            } catch (err) {
              console.error('Error fetching ticker basics for market_type:', err);
              // Continue without market_type - will show full UI
            }
            
            setError(null);
            setLoading(false);
            console.log(`TickerAnalystViews: Successfully fetched data for ${ticker}`);
            
            // Extract the date from the latest quarter's analyst views
            if (analystViewsResponse.data.quarters && analystViewsResponse.data.quarters.length > 0) {
              const latestQuarter = analystViewsResponse.data.quarters[0];
              const latestQuarterViews = analystViewsResponse.data.analystViews.filter(view => 
                view.year === latestQuarter.year && view.quarter === latestQuarter.quarter
              );
              
              if (latestQuarterViews.length > 0) {
                // Get the date from the first analyst view record (they should all have the same date for the quarter)
                setLatestAnalystViewDate(latestQuarterViews[0].date);
              }
            }
            
            // Fetch price data for Last Closing Price and Potential Upside calculations
            await fetchPriceData(ticker);
          } else {
            setError(analystViewsResponse.message || 'Failed to fetch analyst views data');
            setLoading(false);
          }
          
          setIsSyncing(false);
          stopPolling();
          isCheckingSyncRef.current = false; // Reset flag
        } else {
          // If we've reached the polling limit, try to fetch data anyway
          if (attemptNumber >= 5) {
            console.warn(`Sync polling limit reached (5 attempts) for ticker: ${ticker}. Attempting to fetch data anyway.`);
            setPollingLimitReached(true);
            stopPolling();
            
            // Still try to fetch analyst views data even though sync status is false
            try {
              const analystViewsResponse = await tickerApi.getPast4QuartersAnalystViews({
                ticker_basics_id: tickerData.ticker_basics_id,
                category: 'all',
                key: 'all'
              });

              if (analystViewsResponse.success && analystViewsResponse.data) {
                setData(analystViewsResponse.data);
                setTickerBasicsId(tickerData.ticker_basics_id);
                
                // Fetch market_type from ticker basics
                try {
                  const tickerBasicsResponse = await tickerApi.getTickerBasics([ticker.toUpperCase()]);
                  if (tickerBasicsResponse.success && tickerBasicsResponse.data && tickerBasicsResponse.data.length > 0) {
                    const tickerBasic = tickerBasicsResponse.data.find((tb: any) => tb.tickerSymbol === ticker.toUpperCase());
                    if (tickerBasic) {
                      if (tickerBasic.marketType) {
                        setMarketType(tickerBasic.marketType);
                        sessionStorage.setItem(`ticker-market-type-${ticker}`, tickerBasic.marketType);
                      }
                      if (tickerBasic.nextEarningsDate) {
                        const date = typeof tickerBasic.nextEarningsDate === 'string' 
                          ? new Date(tickerBasic.nextEarningsDate) 
                          : tickerBasic.nextEarningsDate;
                        if (date instanceof Date && !isNaN(date.getTime())) {
                          setNextEarningsDate(date);
                        }
                      }
                    }
                  }
                } catch (err) {
                  console.error('Error fetching ticker basics for market_type:', err);
                }
                
                setError(null);
                setLoading(false);
                
                // Extract the date from the latest quarter's analyst views
                if (analystViewsResponse.data.quarters && analystViewsResponse.data.quarters.length > 0) {
                  const latestQuarter = analystViewsResponse.data.quarters[0];
                  const latestQuarterViews = analystViewsResponse.data.analystViews.filter(view => 
                    view.year === latestQuarter.year && view.quarter === latestQuarter.quarter
                  );
                  
                  if (latestQuarterViews.length > 0) {
                    setLatestAnalystViewDate(latestQuarterViews[0].date);
                  }
                }
                
                // Fetch price data
                await fetchPriceData(ticker);
                console.log(`TickerAnalystViews: Successfully fetched data for ${ticker} despite sync status being false`);
              } else {
                throw new Error(analystViewsResponse.message || 'Failed to fetch analyst views data');
              }
            } catch (fetchErr) {
              console.error(`TickerAnalystViews: Failed to fetch data for ${ticker} after polling limit:`, fetchErr);
              setError(fetchErr instanceof Error ? fetchErr.message : 'Failed to fetch ticker data');
              setLoading(false);
            }
            isCheckingSyncRef.current = false; // Reset flag
          } else {
            // Double-check we haven't stopped polling before setting up interval
            if (pollingStoppedRef.current) {
              console.log(`TickerAnalystViews: Polling stopped, not setting up interval`);
              isCheckingSyncRef.current = false; // Reset flag before returning
              return;
            }
            
            console.log(`TickerAnalystViews: ${ticker} is not synced yet (attempt ${attemptNumber}/5), continuing to poll`);
            setIsSyncing(true);
            
            // Update the current attempt ref so the interval callback knows the next attempt number
            currentAttemptRef.current = attemptNumber;
            
            // Continue polling with 2 second delay
            const pollingDelay = 2000; // 2 seconds
            
            // Clear any existing interval before creating a new one
            clearPollingInterval();
            
            console.log(`Starting sync polling with ${pollingDelay}ms delay`);
            const interval = setInterval(() => {
              // Check if polling was stopped or we've reached the limit
              if (pollingStoppedRef.current) {
                clearInterval(interval);
                syncPollingIntervalRef.current = null;
                return;
              }
              
              // Get the next attempt number from the ref
              const nextAttempt = currentAttemptRef.current + 1;
              
              // Check if we've exceeded the limit
              if (nextAttempt > 5) {
                console.log(`TickerAnalystViews: Reached attempt limit (5), stopping interval`);
                clearInterval(interval);
                syncPollingIntervalRef.current = null;
                pollingStoppedRef.current = true;
                return;
              }
              
              // Update the ref for the next iteration
              currentAttemptRef.current = nextAttempt;
              
              // Make the sync check call
              checkSyncAndFetchData(nextAttempt);
            }, pollingDelay);
            
            syncPollingIntervalRef.current = interval;
            
            // Reset flag after setting up interval
            isCheckingSyncRef.current = false;
          }
        }
      } else {
        setError('404 - Ticker not found');
        setIsSyncing(false);
        setLoading(false);
        stopPolling();
        isCheckingSyncRef.current = false; // Reset flag
      }
    } catch (error) {
      console.error(`TickerAnalystViews: Error checking sync status for ${ticker}:`, error);
      
      // Check if it's a 429 rate limit error
      const isRateLimitError = 
        (axios.isAxiosError(error) && error.response?.status === 429) ||
        (error instanceof Error && (error.message.includes('429') || error.message.includes('rate limit') || error.message.includes('Rate limit')));
      
      if (isRateLimitError) {
        console.warn(`TickerAnalystViews: Rate limit (429) detected for ${ticker}. Stopping polling and attempting to fetch data anyway.`);
        stopPolling();
        setPollingLimitReached(true);
        
        // Still try to fetch analyst views data even after rate limit error
        try {
          if (tickerBasicsId) {
            const analystViewsResponse = await tickerApi.getPast4QuartersAnalystViews({
              ticker_basics_id: tickerBasicsId,
              category: 'all',
              key: 'all'
            });

            if (analystViewsResponse.success && analystViewsResponse.data) {
              setData(analystViewsResponse.data);
              
              // Fetch market_type and nextEarningsDate from ticker basics
              try {
                const tickerBasicsResponse = await tickerApi.getTickerBasics([ticker.toUpperCase()]);
                if (tickerBasicsResponse.success && tickerBasicsResponse.data && tickerBasicsResponse.data.length > 0) {
                  const tickerBasic = tickerBasicsResponse.data.find((tb: any) => tb.tickerSymbol === ticker.toUpperCase());
                  if (tickerBasic) {
                    if (tickerBasic.marketType) {
                      setMarketType(tickerBasic.marketType);
                      sessionStorage.setItem(`ticker-market-type-${ticker}`, tickerBasic.marketType);
                    }
                    if (tickerBasic.nextEarningsDate) {
                      const date = typeof tickerBasic.nextEarningsDate === 'string' 
                        ? new Date(tickerBasic.nextEarningsDate) 
                        : tickerBasic.nextEarningsDate;
                      if (date instanceof Date && !isNaN(date.getTime())) {
                        setNextEarningsDate(date);
                      }
                    }
                  }
                }
              } catch (err) {
                console.error('Error fetching ticker basics for market_type:', err);
              }
              
              setError(null);
              setLoading(false);
              
              // Extract the date from the latest quarter's analyst views
              if (analystViewsResponse.data.quarters && analystViewsResponse.data.quarters.length > 0) {
                const latestQuarter = analystViewsResponse.data.quarters[0];
                const latestQuarterViews = analystViewsResponse.data.analystViews.filter(view => 
                  view.year === latestQuarter.year && view.quarter === latestQuarter.quarter
                );
                
                if (latestQuarterViews.length > 0) {
                  setLatestAnalystViewDate(latestQuarterViews[0].date);
                }
              }
              
              // Fetch price data
              await fetchPriceData(ticker);
              console.log(`TickerAnalystViews: Successfully fetched data for ${ticker} despite rate limit error`);
            } else {
              throw new Error(analystViewsResponse.message || 'Failed to fetch analyst views data');
            }
          } else {
            throw new Error('Ticker basics ID not available');
          }
        } catch (fetchErr) {
          console.error(`TickerAnalystViews: Failed to fetch data for ${ticker} after rate limit error:`, fetchErr);
          setError('Rate limit exceeded. Please try again later.');
          setLoading(false);
        }
        isCheckingSyncRef.current = false; // Reset flag
      } else {
        const rawMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        const isTickerNotFound = /no valid ticker|valid tickers found/i.test(rawMessage);
        setError(isTickerNotFound ? '404 - Ticker not found' : rawMessage);
        setIsSyncing(false);
        setLoading(false);
        stopPolling();
        isCheckingSyncRef.current = false; // Reset flag
      }
    }
  };

  // Initial data fetch
  useEffect(() => {
    if (!ticker) return;

    // Reset polling state when ticker changes
    pollingStoppedRef.current = false;
    setPollingLimitReached(false);
    setSyncPollingAttempts(0);
    currentAttemptRef.current = 1; // Reset attempt counter
    isCheckingSyncRef.current = false; // Reset concurrent check flag
    clearPollingInterval(); // Clear any existing interval without setting stopped flag

    setLoading(true);
    setError(null);
    setIsSyncing(false);

    checkSyncAndFetchData(1);

    return () => {
      stopPolling();
    };
  }, [ticker]);

  // Load user ticker preferences when tickerBasicsId is available and user is authenticated
  useEffect(() => {
    const loadPreferences = async () => {
      if (isAuthenticated && tickerBasicsId) {
        try {
          const preferences = await loadUserTickerPreferences(tickerBasicsId, 'analysts');
          setHiddenStatistics(preferences.hiddenStatistics); // Always set, even if empty array
          
          if (preferences.hiddenStatistics.length > 0) {
            console.log('✅ Loaded analyst ticker preferences:', preferences.hiddenStatistics);
          } else {
            console.log('✅ No analyst ticker preferences found, showing all indicators');
          }
        } catch (error) {
          console.log('No analyst ticker preferences found or error loading:', error);
          setHiddenStatistics([]); // Reset to show all indicators when no preferences
        }
      }
      // For unauthenticated users, keep the session storage values that were loaded during initialization
      // Don't reset them here as they were already loaded from sessionStorage in useState
    };

    loadPreferences();
  }, [isAuthenticated, tickerBasicsId]);

  // Reset horizontal scroll to left when switching tabs
  useEffect(() => {
    tableScrollContainerRef.current && (tableScrollContainerRef.current.scrollLeft = 0);
  }, [location.pathname]);

  // Edit mode handlers
  const handleStartEdit = () => {
    setTempHiddenStatistics([...hiddenStatistics]);
    setIsEditingColumns(true);
  };

  const handleCancelEdit = () => {
    setTempHiddenStatistics([...hiddenStatistics]);
    setIsEditingColumns(false);
  };

  const handleDoneEdit = async () => {
    setHiddenStatistics([...tempHiddenStatistics]);
    sessionStorage.setItem(`stocktable-hidden-statistics-${ticker}-analyst-views`, JSON.stringify(tempHiddenStatistics));
    
    // Save user ticker preferences for authenticated users (always save, even if empty array)
    if (isAuthenticated && tickerBasicsId) {
      try {
        await saveUserTickerPreferences(tickerBasicsId, 'analysts', tempHiddenStatistics);
        console.log('✅ Analyst ticker preferences saved successfully');
      } catch (error) {
        console.error('❌ Failed to save analyst ticker preferences:', error);
        // Don't throw error to prevent UI from breaking
      }
    }
    
    setIsEditingColumns(false);
  };

  // Handle category info click
  const handleCategoryInfoClick = (category: string) => {
    setSelectedCategory(category);
    setIsModalOpen(true);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCategory('');
  };

  const handleIndicatorInfoClick = (indicatorKey: string) => {
    setSelectedIndicatorKey(indicatorKey);
    setIsIndicatorModalOpen(true);
  };

  const handleCloseIndicatorModal = () => {
    setIsIndicatorModalOpen(false);
    setSelectedIndicatorKey('');
  };

  const handleHideStatistic = (statisticKey: string) => {
    setTempHiddenStatistics(prev => [...prev, statisticKey]);
  };

  const handleShowStatistic = (statisticKey: string) => {
    setTempHiddenStatistics(prev => prev.filter(key => key !== statisticKey));
  };

  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 640);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const statisticsColumnWidth = isSmallScreen
    ? calculateTickerTechnicalsStatisticsColumnWidth(isEditingColumns, isSmallScreen)
    : calculateAnalystViewsStatisticsColumnWidth(isEditingColumns);

  if (error) {
    return (
      <>
      <div className="flex flex-col min-h-screen bg-background-light dark:bg-background">
        <Header sticky={false} />
        {publicUuid && !isAuthenticated && <PublicListPromoBar />}
        <main className="flex-1 p-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-red-600 dark:text-red-400 mb-2">Error</h2>
              <p className="text-gray-600 dark:text-text-secondary">{error}</p>
            </div>
          </div>
        </main>
        <div className="mt-4 sm:mt-8">
          <Footer />
        </div>
      </div>
      </>
    );
  }


  // Check if ticker is ETF or ETV
  const isEtfOrEtv = marketType && (marketType.toUpperCase() === 'ETF' || marketType.toUpperCase() === 'ETV');
  
  // Filter visible statistics
  const currentStatistics = isEditingColumns 
    ? ANALYST_VIEWS_STATISTICS 
    : ANALYST_VIEWS_STATISTICS.filter(stat => !hiddenStatistics.includes(stat.key));
  
  // Get the current hidden state (temporary in edit mode, permanent in normal mode)
  const currentHiddenStatistics = isEditingColumns ? tempHiddenStatistics : hiddenStatistics;
  
  // Adjust category headers based on filtered statistics
  const currentCategoryHeaders = CATEGORY_HEADERS.map(header => {
    if (isEditingColumns) {
      // In edit mode, use original startIndex since we're showing all statistics
      console.log(`Category "${header.label}": Edit mode - using original startIndex: ${header.startIndex}`);
      return {
        ...header,
        startIndex: header.startIndex
      };
    } else {
      // In normal mode, find the first visible statistic in this category
      const nextCategoryStartIndex = CATEGORY_HEADERS.find(h => h.startIndex > header.startIndex)?.startIndex || ANALYST_VIEWS_STATISTICS.length;
      const categoryStatistics = ANALYST_VIEWS_STATISTICS.slice(header.startIndex, nextCategoryStartIndex);
      
      console.log(`Category "${header.label}":`, {
        originalStartIndex: header.startIndex,
        nextCategoryStartIndex,
        categoryStatistics: categoryStatistics.map(s => s.key),
        hiddenStatistics,
        isEditingColumns
      });
      
      // Find the first visible statistic in this category
      const firstVisibleStatistic = categoryStatistics.find(stat => !hiddenStatistics.includes(stat.key));
      
      if (!firstVisibleStatistic) {
        // No visible statistics in this category - hide the category
        console.log(`Category "${header.label}": No visible statistics`);
        return { 
          ...header, 
          startIndex: -1 
        };
      }
      
      // Find the position of this statistic in the filtered currentStatistics array
      const newStartIndex = currentStatistics.findIndex(stat => stat.key === firstVisibleStatistic.key);
      
      console.log(`Category "${header.label}": First visible statistic: ${firstVisibleStatistic.key}, newStartIndex: ${newStartIndex}`);
      
      return {
        ...header,
        startIndex: newStartIndex
      };
    }
  }).filter(header => {
    // In edit mode, show all categories. In normal mode, only show categories with visible statistics
    const shouldShow = isEditingColumns || header.startIndex !== -1;
    console.log(`Category "${header.label}": shouldShow=${shouldShow}, startIndex=${header.startIndex}, isEditingColumns=${isEditingColumns}`);
    return shouldShow;
  });

  // Navigation tabs
  const tabs = (
    <TabBar 
      activeTab="analysts" 
      ticker={ticker}
      disabled={isEditingColumns}
      publicUuid={publicUuid}
    />
  );

  return (
    <>
    <style dangerouslySetInnerHTML={{
      __html: `
        /* Sticky header bottom border - using a class-based approach */
        table thead.sticky-header-border::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 1px;
          background-color: rgb(229, 231, 235);
          z-index: 51;
          pointer-events: none;
        }
        
        .dark table thead.sticky-header-border::after {
          background-color: rgb(55, 65, 81);
        }
        
        /* Alternative: target thead with position sticky using attribute selector */
        table thead[style*="sticky"]::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 1px;
          background-color: rgb(229, 231, 235);
          z-index: 51;
          pointer-events: none;
        }
        
        .dark table thead[style*="sticky"]::after {
          background-color: rgb(55, 65, 81);
        }
      `
    }} />
    <div className="flex flex-col min-h-screen bg-background-light dark:bg-background">
      {!isFullScreen && <Header sticky={false} />}
      {!isFullScreen && publicUuid && !isAuthenticated && <PublicListPromoBar />}

      <main className="px-1 lg:px-2 xl:px-4 pt-4 sm:pt-8 pb-4 sm:pb-8">
        <div className="bg-surface-light dark:bg-background-card border border-border-light dark:border-border-color rounded-md shadow-lg overflow-hidden">
          {/* Back button */}
          <div className="p-4">
            <button
              onClick={() => {
                if (publicUuid) {
                  navigate(`/analysts?public_uuid=${publicUuid}`);
                } else {
                  navigate('/analysts');
                }
              }}
              className={`flex items-center ${getTextSizeClass(fontSize)} text-text-light-neutral dark:text-text-secondary hover:text-text-light-primary dark:hover:text-text-primary transition-colors`}
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to List
            </button>
          </div>

          {/* Header */}
          <div className="px-4 pb-4">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-semibold text-text-light-primary dark:text-text-dark-primary">
                {tickerName ? `${tickerName} (${tickerSymbol})` : `${tickerSymbol} - Analysts`}
              </h1>
              {marketType !== null && !isEtfOrEtv && !isEditingColumns ? (
                <button
                  onClick={handleStartEdit}
                  className="transition-colors duration-200 text-gray-600 dark:text-text-secondary hover:text-gold-text-light dark:hover:text-highlight-gold"
                  title="Edit statistics"
                >
                  <svg className="lucide lucide-pencil" fill="none" height="20" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="20" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"></path>
                    <path d="m15 5 4 4"></path>
                  </svg>
                </button>
              ) : marketType !== null && !isEtfOrEtv && isEditingColumns ? (
                <div className="flex items-center gap-4">
                  <button
                    onClick={handleCancelEdit}
                    className="text-text-light-neutral dark:text-text-secondary hover:text-text-light-primary dark:hover:text-text-primary transition-colors duration-200 text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDoneEdit}
                    className="bg-primary text-gray-900 px-3 py-1.5 rounded-md text-sm font-medium hover:bg-opacity-90 transition-colors duration-200"
                  >
                    Done
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          {/* Navigation Tabs */}
          <nav className="border-b border-gray-200 dark:border-border-color">
            <div className="px-4">
              {tabs}
            </div>
          </nav>

          {/* Analysts Data Table */}
          {isEtfOrEtv ? (
            <div className="p-8 text-center">
              <p className="text-gray-600 dark:text-text-secondary text-base">
                No analyst estimates or recommendations are available for this ticker.
              </p>
            </div>
          ) : (
          <div ref={tableScrollContainerRef} className="w-full overflow-x-auto overflow-y-auto max-h-[80vh]">
            <table className="min-w-full font-mono" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{
                  width: `${statisticsColumnWidth}px`,
                  minWidth: isSmallScreen ? '100px' : '200px',
                  maxWidth: '500px',
                }} />
                {(isSyncing && (!data || !data.quarters || data.quarters.length === 0) ? Array.from({ length: 4 }) : data?.quarters || []).map((_, index) => (
                  <col key={index} style={{ width: '150px' }} />
                ))}
              </colgroup>
              
              <thead 
                className="bg-gray-50 dark:bg-background-card sticky-header-border" 
                style={{ 
                  position: 'sticky', 
                  top: 0, 
                  zIndex: 50
                }}
              >
                <tr>
                  <th 
                    className={`py-1 px-4 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-12 sm:h-16 bg-gray-50 dark:bg-background-card`}
                    style={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 40,
                      width: `${statisticsColumnWidth}px`,
                      maxWidth: `${statisticsColumnWidth}px`,
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word'
                    }}
                  >
                    INDICATORS
                  </th>
                  {isSyncing && (!data || !data.quarters || data.quarters.length === 0) ? (
                    // Show placeholder column headers during retry when no data available yet
                    Array.from({ length: 4 }).map((_, index) => (
                      <th
                        key={`loading-header-${index}`}
                        className={`py-1 px-1 sm:px-2 md:px-4 lg:px-6 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-12 sm:h-16 bg-gray-50 dark:bg-background-card`}
                      >
                        <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-20 rounded"></div>
                      </th>
                    ))
                  ) : (
                    data?.quarters?.map((quarter, index) => (
                      <th
                        key={index}
                        className={`py-1 px-1 sm:px-2 md:px-4 lg:px-6 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-12 sm:h-16 bg-gray-50 dark:bg-background-card`}
                      >
                        <div className="flex flex-col items-start">
                          <span>{index === 0 ? 'Latest' : formatQuarterHeader(quarter)}</span>
                          {index === 0 && latestAnalystViewDate && (
                            <span className="text-xs text-gray-500 dark:text-text-secondary font-normal mt-1">
                              {formatAnalystViewsDate(latestAnalystViewDate)}
                            </span>
                          )}
                        </div>
                      </th>
                    ))
                  )}
                  
                  {/* Full screen button column */}
                  {!isEtfOrEtv && (
                    <th 
                      className="py-1 px-2 sm:px-4 text-right bg-gray-50 dark:bg-background-card"
                      style={{ position: 'sticky', right: 0, zIndex: 40 }}
                    >
                      {!isEditingColumns && (
                        <button
                          onClick={() => setIsFullScreen(true)}
                          className="p-1.5 text-gray-500 dark:text-white dark:hover:text-highlight-gold transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-border-color rounded-md"
                          title="Open in full screen"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                          </svg>
                        </button>
                      )}
                    </th>
                  )}
                </tr>
              </thead>
              
              <tbody className="bg-surface-light dark:bg-background-card divide-y divide-gray-200 dark:divide-border-color">
                {isSyncing && (!data || !data.quarters || data.quarters.length === 0) ? (
                  // Show placeholder rows with loading animation during retry when no data available yet
                  currentCategoryHeaders.map((categoryHeader, categoryIndex) => {
                    const categoryStats = currentStatistics.slice(
                      categoryHeader.startIndex,
                      categoryIndex < currentCategoryHeaders.length - 1 ? currentCategoryHeaders[categoryIndex + 1].startIndex : currentStatistics.length
                    );
                    
                    return (
                      <React.Fragment key={`loading-${categoryHeader.label}`}>
                        {/* Category Header Row */}
                        <tr className="bg-gray-100 dark:bg-border-color">
                          <td
                            className="px-4 py-1 sm:py-2 text-sm font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color"
                            style={{
                              position: 'sticky',
                              left: 0,
                              zIndex: 30,
                              width: `${statisticsColumnWidth}px`,
                              maxWidth: `${statisticsColumnWidth}px`
                            }}
                          >
                            <div 
                              className="whitespace-nowrap"
                              style={{ overflow: 'visible', position: 'relative' }}
                            >
                              <span className="inline">{categoryHeader.label}</span>
                              <button
                                onClick={() => handleCategoryInfoClick(categoryHeader.label)}
                                className="inline-flex items-center justify-center w-5 h-5 ml-1 rounded-full text-gray-900 dark:text-text-primary hover:text-primary dark:hover:text-highlight-gold hover:bg-primary/10 dark:hover:bg-highlight-gold/10 transition-colors align-middle"
                                title={`Learn more about ${categoryHeader.label}`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                                  <path d="M12 16v-4" strokeWidth="2" strokeLinecap="round"/>
                                  <path d="M12 8h.01" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                              </button>
                            </div>
                          </td>
                          {Array.from({ length: 4 }).map((_, colIndex) => (
                            <td
                              key={`loading-header-col-${colIndex}`}
                              className="px-2 sm:px-4 py-1 sm:py-2 text-sm font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color"
                            >
                              {/* Empty cells for alignment */}
                            </td>
                          ))}
                          <td className="px-2 sm:px-4 py-1 sm:py-2 text-sm font-semibold text-gray-900 dark:text-text-primary">
                            {/* Empty cell for full screen button column */}
                          </td>
                        </tr>
                        
                        {/* Statistics Rows */}
                        {categoryStats.map((statistic) => (
                          <tr key={`loading-${statistic.key}`} className="h-12 sm:h-16">
                            <td 
                              className="px-4 text-gray-900 dark:text-text-primary font-medium bg-surface-light dark:bg-background-card"
                              style={{
                                position: 'sticky',
                                left: 0,
                                zIndex: 30,
                                width: `${statisticsColumnWidth}px`,
                                maxWidth: `${statisticsColumnWidth}px`,
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word',
                                hyphens: 'auto'
                              }}
                            >
                              <div className={`flex items-start ${getStatisticLabelSizeClass(fontSize)}`}>
                                {statistic.label.split('\n').map((line, lineIndex) => {
                                  const words = line.split(' ');
                                  const lastWordIndex = words.length - 1;
                                  return (
                                    <span key={lineIndex} className={lineIndex > 0 ? 'block' : ''}>
                                      {words.map((word, wordIndex) => (
                                        <span key={wordIndex}>
                                          {wordIndex > 0 && ' '}
                                          {wordIndex === lastWordIndex ? (
                                            <span className="whitespace-nowrap">
                                              {word}
                                              {lineIndex === statistic.label.split('\n').length - 1 && wordIndex === lastWordIndex && (statistic.key === 'priceTargetDifference' || statistic.key === 'averageRecommendation') && (
                                                <button
                                                  onClick={() => handleIndicatorInfoClick(statistic.key)}
                                                  className="inline-flex items-center justify-center w-4 h-4 ml-1 rounded-full text-gray-600 dark:text-text-secondary hover:text-primary dark:hover:text-highlight-gold hover:bg-primary/10 dark:hover:bg-highlight-gold/10 transition-colors align-middle"
                                                  title={`Learn more about ${statistic.label}`}
                                                >
                                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                                                    <path d="M12 16v-4" strokeWidth="2" strokeLinecap="round"/>
                                                    <path d="M12 8h.01" strokeWidth="2" strokeLinecap="round"/>
                                                  </svg>
                                                </button>
                                              )}
                                            </span>
                                          ) : (
                                            word
                                          )}
                                        </span>
                                      ))}
                                      {lineIndex < statistic.label.split('\n').length - 1 && <br />}
                                    </span>
                                  );
                                })}
                              </div>
                            </td>
                            {Array.from({ length: 4 }).map((_, quarterIndex) => (
                              <td key={`loading-quarter-${quarterIndex}`} className={`px-1 sm:px-2 md:px-4 lg:px-6 py-1 sm:py-2 text-left font-semibold ${getTextSizeClass(fontSize)} bg-surface-light dark:bg-background-card`}>
                                <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-16 rounded"></div>
                              </td>
                            ))}
                            <td className="px-2 sm:px-4 py-1 sm:py-2 text-sm font-semibold text-gray-900 dark:text-text-primary">
                              {/* Empty cell for full screen button column */}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })
                ) : (
                  currentCategoryHeaders.map((categoryHeader, categoryIndex) => (
                    <React.Fragment key={categoryHeader.label}>
                      {/* Category Header Row */}
                      <tr className="bg-gray-100 dark:bg-border-color">
                        <td
                          className={`px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}
                          style={{
                            position: 'sticky',
                            left: 0,
                            zIndex: 30,
                            width: `${statisticsColumnWidth}px`,
                            maxWidth: `${statisticsColumnWidth}px`
                          }}
                        >
                          <div 
                            className="whitespace-nowrap"
                            style={{ overflow: 'visible', position: 'relative' }}
                          >
                            <span className="inline">{categoryHeader.label}</span>
                            <button
                              onClick={() => handleCategoryInfoClick(categoryHeader.label)}
                              className="inline-flex items-center justify-center w-5 h-5 ml-1 rounded-full text-gray-900 dark:text-text-primary hover:text-primary dark:hover:text-highlight-gold hover:bg-primary/10 dark:hover:bg-highlight-gold/10 transition-colors align-middle"
                              title={`Learn more about ${categoryHeader.label}`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                                <path d="M12 16v-4" strokeWidth="2" strokeLinecap="round"/>
                                <path d="M12 8h.01" strokeWidth="2" strokeLinecap="round"/>
                              </svg>
                            </button>
                          </div>
                        </td>
                        {data?.quarters?.map((_, colIndex) => (
                          <td
                            key={colIndex}
                            className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}
                          >
                            {/* Empty cells for alignment */}
                          </td>
                        ))}
                        
                        {/* Empty cell for full screen button column */}
                        <td className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary`}>
                          {/* Empty cell for full screen button column */}
                        </td>
                      </tr>
                      
                      {/* Statistics Rows */}
                      {currentStatistics
                        .slice(categoryHeader.startIndex, categoryIndex < currentCategoryHeaders.length - 1 ? currentCategoryHeaders[categoryIndex + 1].startIndex : currentStatistics.length)
                        .map((statistic, statIndex) => {
                          const isHidden = currentHiddenStatistics.includes(statistic.key);
                          return (
                            <tr key={statistic.key} className={`h-12 sm:h-16 ${isEditingColumns && isHidden ? 'opacity-50' : ''}`}>
                              <td 
                                className="px-4 text-gray-900 dark:text-text-primary font-medium bg-surface-light dark:bg-background-card"
                                style={{
                                  position: 'sticky',
                                  left: 0,
                                  zIndex: 30,
                                  width: `${statisticsColumnWidth}px`,
                                  maxWidth: `${statisticsColumnWidth}px`,
                                  wordWrap: 'break-word',
                                  overflowWrap: 'break-word',
                                  hyphens: 'auto'
                                }}
                              >
                                <div className="flex items-center gap-2">
                                  {isEditingColumns && (
                                    <button
                                      onClick={isHidden ? () => handleShowStatistic(statistic.key) : () => handleHideStatistic(statistic.key)}
                                      className={`transition-all duration-200 p-1 rounded-full active:scale-90 ${
                                        isHidden 
                                          ? 'text-gray-600 dark:text-text-secondary hover:text-highlight-gold hover:bg-highlight-gold/10' 
                                          : 'text-gray-600 dark:text-text-secondary hover:text-market-red hover:bg-market-red/10'
                                      }`}
                                      title={isHidden ? "Show statistic" : "Hide statistic"}
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        {isHidden ? (
                                          // Unchecked rounded square (hidden)
                                          <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" />
                                        ) : (
                                          // Checked rounded square (visible)
                                          <>
                                            <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" fill="white" className="dark:fill-gray-800" />
                                            <path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                          </>
                                        )}
                                      </svg>
                                    </button>
                                  )}
                                  <div className={`flex items-start ${getStatisticLabelSizeClass(fontSize)}`}>
                                    {statistic.label.split('\n').map((line, lineIndex) => {
                                      const words = line.split(' ');
                                      const lastWordIndex = words.length - 1;
                                      return (
                                        <span key={lineIndex} className={lineIndex > 0 ? 'block' : ''}>
                                          {words.map((word, wordIndex) => (
                                            <span key={wordIndex}>
                                              {wordIndex > 0 && ' '}
                                              {wordIndex === lastWordIndex ? (
                                                <span className="whitespace-nowrap">
                                                  {word}
                                                  {PRO_ONLY_STATISTICS.includes(statistic.key) && (
                                                    <span className="ml-1.5 px-1.5 py-0.5 text-xs font-semibold text-gray-900 bg-primary/30 dark:bg-highlight-gold/20 dark:text-highlight-gold rounded">
                                                      PRO
                                                    </span>
                                                  )}
                                                  {lineIndex === statistic.label.split('\n').length - 1 && wordIndex === lastWordIndex && (statistic.key === 'priceTargetDifference' || statistic.key === 'averageRecommendation') && (
                                                    <button
                                                      onClick={() => handleIndicatorInfoClick(statistic.key)}
                                                      className="inline-flex items-center justify-center w-4 h-4 ml-1 rounded-full text-gray-600 dark:text-text-secondary hover:text-primary dark:hover:text-highlight-gold hover:bg-primary/10 dark:hover:bg-highlight-gold/10 transition-colors align-middle"
                                                      title={`Learn more about ${statistic.label}`}
                                                    >
                                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                                                        <path d="M12 16v-4" strokeWidth="2" strokeLinecap="round"/>
                                                        <path d="M12 8h.01" strokeWidth="2" strokeLinecap="round"/>
                                                      </svg>
                                                    </button>
                                                  )}
                                                </span>
                                              ) : (
                                                word
                                              )}
                                            </span>
                                          ))}
                                          {lineIndex < statistic.label.split('\n').length - 1 && <br />}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </div>
                              </td>
                              {data?.quarters?.map((quarter, quarterIndex) => {
                                const quarterData = getAnalystViewsQuarterData(quarter, quarterIndex, data, priceData, nextEarningsDate);
                                const value = quarterData[statistic.key];
                                const formattedValue = formatAnalystViewsValue(value, statistic.key);
                                const color = getAnalystViewsStatisticColor(statistic.key, value);
                                // Check if locked and has a real value
                                const isLocked = lockedStatistics.includes(statistic.key) && value !== null && value !== undefined && formattedValue !== '-';
                                
                                return (
                                  <td key={quarterIndex} className={`px-1 sm:px-2 md:px-4 lg:px-6 py-1 sm:py-2 text-left font-semibold ${getTextSizeClass(fontSize)} ${color}`}>
                                    {loading || isSyncing ? (
                                      <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-16 rounded"></div>
                                    ) : isLocked ? (
                                      <LockIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" onClick={!isPublicList ? () => navigate('/pricing', { state: { fromUpgrade: true } }) : undefined} tooltipText={isPublicList ? "Locked due to the list owner's plan" : "Upgrade to the Pro Plan to see"} />
                                    ) : (
                                      formattedValue
                                    )}
                                  </td>
                                );
                              })}
                              
                              {/* Empty cell for full screen button column */}
                              <td className="px-2 sm:px-4 py-1 sm:py-2 text-sm font-semibold text-gray-900 dark:text-text-primary">
                                {/* Empty cell for full screen button column */}
                              </td>
                            </tr>
                          );
                        })}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
          )}
          
          {/* Status Bar */}
          <div className={`px-4 py-2 border-t border-gray-200 dark:border-border-color bg-gray-50 dark:bg-black/10 flex justify-between items-center ${getSmallTextSizeClass(fontSize)} text-gray-600 dark:text-text-secondary rounded-b-md`}>
            <div className="flex items-center gap-4">
              {loading && (
                <div className="flex items-center gap-2">
                  <div className="animate-spin w-3 h-3 border border-highlight-gold border-t-transparent rounded-full"></div>
                  <span>Loading...</span>
                </div>
              )}
              {lastUpdated && !loading && (
                <span>Last updated: {lastUpdated.toLocaleDateString()} {lastUpdated.toLocaleTimeString()}</span>
              )}
            </div>
          </div>
        </div>
      </main>
      
      {/* Full Screen Overlay */}
      {isFullScreen && (
      <div
        className="fixed inset-0 z-[200] flex min-h-0 flex-col bg-background-light dark:bg-background"
        role="dialog"
        aria-modal="true"
      >
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-contain">
            {isEtfOrEtv ? (
              <div className="min-h-full flex items-center justify-center py-8">
                <p className="text-gray-600 dark:text-text-secondary text-base">
                  No analyst estimates or recommendations are available for this ticker.
                </p>
              </div>
            ) : (
            <table className="min-w-full font-mono" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{
                  width: `${statisticsColumnWidth}px`,
                  minWidth: isSmallScreen ? '100px' : '200px',
                  maxWidth: `${statisticsColumnWidth}px`,
                }} />
                {data?.quarters?.map((_, index) => (
                  <col key={index} style={{ width: '150px' }} />
                )) || []}
              </colgroup>
              
              {/* Header with sticky positioning */}
              <thead className="sticky-header-border" style={{ position: 'sticky', top: 0, zIndex: 50 }}>
                <tr>
                  {/* Statistics column header */}
                  <th 
                    className="py-1 px-4 text-left text-gray-600 dark:text-text-secondary text-xs font-semibold uppercase tracking-wider h-10 bg-gray-50 dark:bg-background-card"
                    style={{ 
                      width: `${statisticsColumnWidth}px`,
                      maxWidth: `${statisticsColumnWidth}px`,
                      position: 'sticky',
                      left: 0,
                      zIndex: 40,
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word'
                    }}
                  >
                    <div className="flex items-center">
                      <span>INDICATORS</span>
                    </div>
                  </th>
                  
                  {/* Quarter columns headers */}
                  {data?.quarters?.map((quarter, index) => (
                    <th
                      key={index}
                      className="py-1 px-2 sm:px-4 lg:px-6 text-left text-gray-600 dark:text-text-secondary text-xs font-semibold uppercase tracking-wider h-10 bg-gray-50 dark:bg-background-card"
                    >
                      <div className="flex flex-col items-start">
                        <span>{index === 0 ? 'Latest' : formatQuarterHeader(quarter)}</span>
                        {index === 0 && latestAnalystViewDate && (
                          <span className="text-xs text-gray-500 dark:text-text-secondary font-normal mt-1">
                            {formatAnalystViewsDate(latestAnalystViewDate)}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                  
                  {/* Close button - sticky right */}
                  <th 
                    className="py-1 px-2 sm:px-4 text-right bg-gray-50 dark:bg-background-card"
                    style={{ position: 'sticky', right: 0, zIndex: 40 }}
                  >
                    <button
                      onClick={() => setIsFullScreen(false)}
                      className="p-1.5 text-gray-500 dark:text-white hover:text-highlight-gold transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-border-color rounded-md"
                      title="Close full screen"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </th>
                </tr>
              </thead>
              
              {/* Table body */}
              <tbody className="bg-surface-light dark:bg-background-card divide-y divide-gray-200 dark:divide-border-color">
                {currentCategoryHeaders.map((categoryHeader, categoryIndex) => (
                  <React.Fragment key={categoryHeader.label}>
                    {/* Category Header Row */}
                    <tr className="bg-gray-100 dark:bg-border-color">
                      <td
                        className="px-4 py-1 sm:py-2 text-sm font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color"
                        style={{
                          position: 'sticky',
                          left: 0,
                          zIndex: 30,
                          width: `${statisticsColumnWidth}px`,
                          maxWidth: `${statisticsColumnWidth}px`
                        }}
                      >
                        <div 
                          className="whitespace-nowrap"
                          style={{ overflow: 'visible', position: 'relative' }}
                        >
                          <span className="inline">{categoryHeader.label}</span>
                          <button
                            onClick={() => handleCategoryInfoClick(categoryHeader.label)}
                            className="inline-flex items-center justify-center w-5 h-5 ml-1 rounded-full text-gray-900 dark:text-text-primary hover:text-primary dark:hover:text-highlight-gold hover:bg-primary/10 dark:hover:bg-highlight-gold/10 transition-colors align-middle"
                            title={`Learn more about ${categoryHeader.label}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                              <path d="M12 16v-4" strokeWidth="2" strokeLinecap="round"/>
                              <path d="M12 8h.01" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                          </button>
                        </div>
                      </td>
                      {data?.quarters?.map((_, colIndex) => (
                        <td
                          key={colIndex}
                          className="px-2 sm:px-4 py-1 sm:py-2 text-sm font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color"
                        >
                          {/* Empty cells for alignment */}
                        </td>
                      ))}
                      
                      {/* Empty cell for close button column */}
                      <td className="px-2 sm:px-4 py-1 sm:py-2 text-sm font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color">
                        {/* Empty cell for close button column */}
                      </td>
                    </tr>
                    
                    {/* Statistics Rows */}
                    {currentStatistics
                      .slice(categoryHeader.startIndex, categoryIndex < currentCategoryHeaders.length - 1 ? currentCategoryHeaders[categoryIndex + 1].startIndex : currentStatistics.length)
                      .map((statistic, statIndex) => {
                        const isHidden = currentHiddenStatistics.includes(statistic.key);
                        return (
                          <tr key={statistic.key} className={`h-10 ${isEditingColumns && isHidden ? 'opacity-50' : ''}`}>
                            <td 
                              className="px-2 sm:px-4 py-1 text-left text-sm font-semibold text-gray-900 dark:text-text-primary bg-white dark:bg-background"
                              style={{
                                position: 'sticky',
                                left: 0,
                                zIndex: 20,
                                width: `${statisticsColumnWidth}px`,
                                maxWidth: `${statisticsColumnWidth}px`,
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word',
                                hyphens: 'auto'
                              }}
                            >
                              <div className="flex items-center gap-2">
                                {isEditingColumns && (
                                  <button
                                    onClick={isHidden ? () => handleShowStatistic(statistic.key) : () => handleHideStatistic(statistic.key)}
                                    className={`transition-all duration-200 p-1 rounded-full active:scale-90 ${
                                      isHidden 
                                        ? 'text-gray-600 dark:text-text-secondary hover:text-highlight-gold hover:bg-highlight-gold/10' 
                                        : 'text-gray-600 dark:text-text-secondary hover:text-market-red hover:bg-market-red/10'
                                    }`}
                                    title={isHidden ? "Show statistic" : "Hide statistic"}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      {isHidden ? (
                                        // Unchecked rounded square (hidden)
                                        <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" />
                                      ) : (
                                        // Checked rounded square (visible)
                                        <>
                                          <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" fill="white" className="dark:fill-gray-800" />
                                          <path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </>
                                      )}
                                    </svg>
                                  </button>
                                )}
                                <div className="flex items-start text-sm sm:text-base">
                                  {statistic.label.split('\n').map((line, lineIndex) => {
                                    const words = line.split(' ');
                                    const lastWordIndex = words.length - 1;
                                    return (
                                      <span key={lineIndex} className={lineIndex > 0 ? 'block' : ''}>
                                        {words.map((word, wordIndex) => (
                                          <span key={wordIndex}>
                                            {wordIndex > 0 && ' '}
                                            {wordIndex === lastWordIndex ? (
                                              <span className="whitespace-nowrap">
                                                {word}
                                                {lineIndex === statistic.label.split('\n').length - 1 && wordIndex === lastWordIndex && (statistic.key === 'priceTargetDifference' || statistic.key === 'averageRecommendation') && (
                                                  <button
                                                    onClick={() => handleIndicatorInfoClick(statistic.key)}
                                                    className="inline-flex items-center justify-center w-4 h-4 ml-1 rounded-full text-gray-600 dark:text-text-secondary hover:text-primary dark:hover:text-highlight-gold hover:bg-primary/10 dark:hover:bg-highlight-gold/10 transition-colors align-middle"
                                                    title={`Learn more about ${statistic.label}`}
                                                  >
                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                      <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                                                      <path d="M12 16v-4" strokeWidth="2" strokeLinecap="round"/>
                                                      <path d="M12 8h.01" strokeWidth="2" strokeLinecap="round"/>
                                                    </svg>
                                                  </button>
                                                )}
                                              </span>
                                            ) : (
                                              word
                                            )}
                                          </span>
                                        ))}
                                        {lineIndex < statistic.label.split('\n').length - 1 && <br />}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            </td>
                            {data?.quarters?.map((quarter, quarterIndex) => {
                              const quarterData = getAnalystViewsQuarterData(quarter, quarterIndex, data, priceData, nextEarningsDate);
                              const value = quarterData[statistic.key];
                              const formattedValue = formatAnalystViewsValue(value, statistic.key);
                              const color = getAnalystViewsStatisticColor(statistic.key, value);
                              // Check if locked and has a real value
                              const isLocked = lockedStatistics.includes(statistic.key) && value !== null && value !== undefined && formattedValue !== '-';
                              
                              return (
                                <td 
                                  key={quarterIndex} 
                                  className={`px-2 sm:px-4 lg:px-6 py-1 text-left text-sm font-semibold bg-white dark:bg-background ${color}`}
                                >
                                  {loading || isSyncing ? (
                                    <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-16 rounded"></div>
                                  ) : isLocked ? (
                                    <LockIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" onClick={!isPublicList ? () => navigate('/pricing', { state: { fromUpgrade: true } }) : undefined} tooltipText={isPublicList ? "Locked due to the list owner's plan" : "Upgrade to the Pro Plan to see"} />
                                  ) : (
                                    formattedValue
                                  )}
                                </td>
                              );
                            })}
                            
                            {/* Empty cell for close button column */}
                            <td className="px-4 py-1 text-sm font-semibold text-gray-900 dark:text-text-primary bg-white dark:bg-background">
                              {/* Empty cell for close button column */}
                            </td>
                          </tr>
                        );
                      })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            )}
        </div>
          
          {/* Status Bar */}
          <div className={`flex-shrink-0 px-4 py-2 border-t border-gray-200 dark:border-border-color bg-gray-50 dark:bg-black/10 flex justify-between items-center ${getSmallTextSizeClass(fontSize)} text-gray-600 dark:text-text-secondary rounded-b-md`}>
            <div className="flex items-center gap-4">
              {loading && (
                <div className="flex items-center gap-2">
                  <div className="animate-spin w-3 h-3 border border-highlight-gold border-t-transparent rounded-full"></div>
                  <span>Loading...</span>
                </div>
              )}
              {lastUpdated && !loading && (
                <span>Last updated: {lastUpdated.toLocaleDateString()} {lastUpdated.toLocaleTimeString()}</span>
              )}
            </div>
          </div>
      </div>
      )}
      
      {/* Footer */}
      <div className="mt-4 sm:mt-8">
        <Footer />
      </div>
      
      {/* Category Info Modal */}
      <CategoryInfoModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        categoryName={selectedCategory}
      />
      
      {/* Indicator Info Modal */}
      <IndicatorInfoModal
        isOpen={isIndicatorModalOpen}
        onClose={handleCloseIndicatorModal}
        indicatorKey={selectedIndicatorKey}
      />
    </div>
    </>
  );
};

export default TickerAnalystViews;
